<template>
  <span
    class="duel-clock"
    :class="{ running, urgent, punch }"
    :style="punch ? { '--clock-punch-ms': punchMs + 'ms' } : undefined"
    :title="t('duel.clockHint')"
  >{{ label }}</span>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from '../i18n/t'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { formatDuelClock, isDuelClockUrgent } from '../ui/duelClockDisplay'

const props = defineProps<{
  remainingMs: number
  /** Server timestamp the snapshot was taken at. */
  at: number
  running?: boolean
}>()

const { t } = useI18n()
const duelPrefs = useDuelPrefsStore()
const now = ref(Date.now())
const punch = ref(false)
const punchMs = ref(520)
let timer: ReturnType<typeof setInterval> | null = null
let punchTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Tick inside the chip. A parent interval re-rendered the whole duel four
 * times a second and, on iPhone, that repaint ate canvas taps in PvP only.
 */
function syncTimer(): void {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  now.value = Date.now()
  if (!props.running) return
  timer = setInterval(() => {
    now.value = Date.now()
  }, 250)
}

watch(
  () => [props.running, props.at, props.remainingMs] as const,
  syncTimer,
  { immediate: true },
)

onUnmounted(() => {
  if (timer) clearInterval(timer)
  if (punchTimer) clearTimeout(punchTimer)
})

const shownMs = computed(() => {
  let ms = props.remainingMs
  if (props.running) ms -= Math.max(0, now.value - props.at)
  return Math.max(0, ms)
})

const label = computed(() => formatDuelClock(shownMs.value))

const urgent = computed(
  () => Boolean(props.running) && isDuelClockUrgent(shownMs.value),
)

function labelSeconds(text: string): number {
  const [min, sec] = text.split(':')
  return Number(min) * 60 + Number(sec)
}

/** Same grow-then-settle as the Life Point punch. A full second step finishes before the next one. */
watch(label, (next, prev) => {
  if (!prev || next === prev) return
  if (duelPrefs.motionLevel !== 'full') return
  const grew = labelSeconds(next) > labelSeconds(prev)
  const ms = grew ? 900 : 520
  punch.value = false
  punchMs.value = ms
  if (punchTimer) clearTimeout(punchTimer)
  requestAnimationFrame(() => {
    punch.value = true
  })
  punchTimer = setTimeout(() => {
    punch.value = false
  }, ms)
})
</script>

<style scoped>
.duel-clock {
  position: relative;
  z-index: 1;
  display: inline-block;
  min-width: 2.4em;
  color: #cbd5e1;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-align: center;
  transform-origin: center center;
}

.duel-clock.running {
  color: #fde047;
}

.duel-clock.urgent {
  color: #f87171;
}

.duel-clock.punch {
  z-index: 3;
  animation: duel-clock-punch var(--clock-punch-ms, 520ms) ease-out;
}

@keyframes duel-clock-punch {
  0% {
    transform: scale(1);
  }
  14% {
    transform: scale(2.2);
  }
  50% {
    transform: scale(1.45);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .duel-clock.punch {
    animation: none;
  }
}
</style>
